import styled from '@emotion/styled'
import { PbsAdUnit } from '@pubstack/common/src/pbs/adunit'
import { FunctionComponent } from 'react'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { Alert } from '~/components/Alert'
import { CodeSnippet } from '~/components/CodeSnippet'
import { Flyout } from '~/components/Flyout'
import { FoldingContent } from '~/components/FoldingContent'
import { Icon } from '~/components/Icon'
import { Link } from '~/components/Link'
import { useToast } from '~/components/Toast/useToasts'
import { useAdFormat } from '~/modules/adm/inventory/adunits/AdFormatDisplay'
import {
  ampIntegrationExample,
  androidBannerIntegrationExample,
  androidInstreamIntegrationExample,
  androidIntegrationNativeExample,
  androidInterstitialIntegrationExample,
  iosInstreamIntegrationExample,
  iosIntegrationBannerExample,
  iosIntegrationInterstitialExample,
  iosIntegrationNativeExample,
} from '~/utils/adUnitsConfiguration'

const Section = styled.div`
  padding: 0.5rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  ${Fonts.P1}
`

const Title = styled.h2`
  ${Fonts.H2}
  font-weight: 500;
`

const AdUnitDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;

  h3 {
    ${Fonts.P2}
    color: ${Colors.SlateGrey};
    font-weight: normal;
  }
    
  & > span {
    text-overflow: ellipsis;
    overflow: hidden;
  }
`

const CodeSnippetWrapper = styled.div`
  code {
    width: 100%;
  }
`

const AdUnitFormat = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`

const getAndroidIntegrationExampleByFormat = (adUnitName: string, adFormatId: string) => {
  const mapping: Record<string, string> = {
    APP_INTERSTITIAL: androidInterstitialIntegrationExample(adUnitName),
    APP_BANNER: androidBannerIntegrationExample(adUnitName),
    APP_INSTREAM: androidInstreamIntegrationExample(adUnitName),
    APP_NATIVE: androidIntegrationNativeExample(adUnitName),
  }
  return mapping[adFormatId]
}

const getIosIntegrationExampleByFormat = (adUnitName: string, adFormatId: string) => {
  const mapping: Record<string, string> = {
    APP_INTERSTITIAL: iosIntegrationInterstitialExample(adUnitName),
    APP_BANNER: iosIntegrationBannerExample(adUnitName),
    APP_INSTREAM: iosInstreamIntegrationExample(adUnitName),
    APP_NATIVE: iosIntegrationNativeExample(adUnitName),
  }
  return mapping[adFormatId]
}

type Props = {
  adUnit: PbsAdUnit
  siteId?: string
}

export const ViewAdUnitFlyout: FunctionComponent<Props> = ({ adUnit, siteId }) => {
  // Hooks
  const toast = useToast()
  const adFormatDisplay = useAdFormat(adUnit.adFormat?.id)

  const copiedToClipboardToast = () => {
    toast.success(
      <>
        <strong>{adUnit.name}</strong> set-up code was copied to your clipboard
      </>
    )
  }

  return (
    <Flyout.Content>
      <Flyout.Title>View ad unit</Flyout.Title>
      <Flyout.Body>
        <Section>
          <Title>Identification</Title>
          <AdUnitDetails>
            <h3>Ad unit name</h3>
            {/* Renaming is unwired for now — see the same note in `PureAdmAdUnitDetailsFlyout`. The modal
                stays in the tree; only the way in is gone. */}
            <span>{adUnit.name}</span>
          </AdUnitDetails>
        </Section>
        {adUnit.adFormat && (
          <Section>
            <Title>Format</Title>
            <AdUnitFormat>
              <Icon name={adFormatDisplay!.iconName} width={'44px'} />
              <span>{adFormatDisplay!.name}</span>
            </AdUnitFormat>
          </Section>
        )}
        <Section>
          <Title>Configuration</Title>

          {adUnit.env === 's2s' && (
            <CodeSnippetWrapper>
              <CodeSnippet
                trackingLabel="pbs-gamManager-view"
                contentText={`{
    bidder: 'pubstack',
    params: {${
      siteId
        ? `
      siteId: '${siteId}',`
        : ''
    }
      adUnitName: '${adUnit.name}',
    }
}
`}
                onCodeCopy={copiedToClipboardToast}
              />
            </CodeSnippetWrapper>
          )}
          {adUnit.env === 'inapp' && (
            <CodeSnippetWrapper>
              <FoldingContent title={'iOS (Swift)'} isFolding={true}>
                <CodeSnippet trackingLabel="pbs-ios-configuration-view" contentText={getIosIntegrationExampleByFormat(adUnit.name, adUnit.adFormat!.id)} onCodeCopy={copiedToClipboardToast} />
              </FoldingContent>

              <FoldingContent title={'Android (Kotlin)'} isFolding={true}>
                <CodeSnippet trackingLabel="pbs-android-configuration-view" contentText={getAndroidIntegrationExampleByFormat(adUnit.name, adUnit.adFormat!.id)} onCodeCopy={copiedToClipboardToast} />
              </FoldingContent>
            </CodeSnippetWrapper>
          )}

          {adUnit.env === 'amp' && (
            <>
              <Alert variant="primary" iconName="info">
                <div>
                  Please be sure that the tag settings match your ad inventory. Need specific setup? Visit the{' '}
                  <Link
                    label={'Pubstack Help Center'}
                    href={`https://docs.pubstack.io/monetization-manager-node/introduction/amp-environment-technical-requirements`}
                    trackingLabel={`pbs-pubstack-help-center-navigate`}
                    variant="secondary"
                    target="_blank"
                  />
                  .
                </div>
              </Alert>
              {!siteId && (
                <Alert variant="warning" iconName="warning">
                  <div>Don't forget to specify the Pubstack Site ID on the snippet code.</div>
                </Alert>
              )}
              <CodeSnippetWrapper>
                <CodeSnippet trackingLabel="pbs-gamManager-view" contentText={ampIntegrationExample(adUnit.name, siteId)} onCodeCopy={copiedToClipboardToast} />
              </CodeSnippetWrapper>
            </>
          )}
        </Section>
      </Flyout.Body>
    </Flyout.Content>
  )
}
